import { Helmet } from 'react-helmet-async';
import { EDITORIAL_TEAM, FOUNDER } from '@/data/authors';

const BASE_URL = 'https://modernnomadliving.com';

// Organization Schema for the site
export const OrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Modern Nomad Living',
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/logo-512.png`,
      width: 512,
      height: 512,
    },
    description: 'Premium knowledge platform for alternative housing and off-grid living',
    foundingDate: '2024',
    knowsAbout: ['Van life', 'Off-grid living', 'Boat living', 'Camping', 'Alternative housing'],
    publishingPrinciples: `${BASE_URL}/about#editorial-standards`,
    diversityPolicy: `${BASE_URL}/about#editorial-standards`,
    ethicsPolicy: `${BASE_URL}/about#editorial-standards`,
    founder: {
      '@type': 'Person',
      name: FOUNDER.name,
      jobTitle: FOUNDER.role,
      description: FOUNDER.shortBio,
      image: FOUNDER.image.startsWith('http') ? FOUNDER.image : `${BASE_URL}${FOUNDER.image}`,
      url: FOUNDER.url,
      knowsAbout: ['Van life', 'Off-grid living', 'Boat living', 'Camping'],
    },
    // sameAs: add verified social profile URLs here when accounts go live.
    sameAs: [],
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Website Schema for search functionality
export const WebsiteSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Modern Nomad Living',
    url: BASE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${BASE_URL}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Article Schema for individual articles
interface ArticleSchemaProps {
  title: string;
  description: string;
  image: string;
  publishedAt: string;
  modifiedAt?: string;
  author?: string;
  shortAnswer?: string;
  url: string;
  wordCount?: number;
  keywords?: string[];
  articleSection?: string;
}

export const ArticleSchema = ({
  title,
  description,
  image,
  publishedAt,
  modifiedAt,
  author,
  shortAnswer,
  url,
  wordCount,
  keywords,
  articleSection,
}: ArticleSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: shortAnswer || description,
    ...(shortAnswer && { abstract: shortAnswer }),
    image: image.startsWith('http') ? image : `${BASE_URL}${image}`,
    datePublished: publishedAt,
    dateModified: modifiedAt || publishedAt,
    author: {
      '@type': 'Organization',
      name: author || EDITORIAL_TEAM.name,
      url: EDITORIAL_TEAM.url,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Modern Nomad Living',
      url: BASE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/logo-512.png`,
        width: 512,
        height: 512,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.short-answer', 'h1', 'h2'],
    },
    ...(wordCount && { wordCount }),
    ...(keywords && keywords.length > 0 && { keywords: keywords.join(', ') }),
    ...(articleSection && { articleSection }),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Breadcrumb Schema for navigation
interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// Collection/ItemList Schema for category pages
interface CollectionSchemaProps {
  name: string;
  description: string;
  url: string;
  items: Array<{
    title: string;
    url: string;
    image: string;
  }>;
}

export const CollectionSchema = ({
  name,
  description,
  url,
  items,
}: CollectionSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: name,
    description: description,
    url: url,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: item.url,
        name: item.title,
        image: item.image.startsWith('http') ? item.image : `${BASE_URL}${item.image}`,
      })),
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// FAQPage Schema for FAQ sections
interface FAQItem {
  question: string;
  answer: string;
}

interface FAQPageSchemaProps {
  faqs: FAQItem[];
}

export const FAQPageSchema = ({ faqs }: FAQPageSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};

// HowTo Schema for step-by-step instructions
interface HowToStep {
  name: string;
  text: string;
}

interface HowToSchemaProps {
  name: string;
  description: string;
  steps: HowToStep[];
}

export const HowToSchema = ({ name, description, steps }: HowToSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
    </Helmet>
  );
};
